import { ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID } from '@syncanix/shared';
import {
  Activity,
  FlaskConical,
  Gauge,
  FileBarChart,
  Filter,
  Route,
  Home,
  LayoutTemplate,
  Megaphone,
  MessagesSquare,
  Palette,
  ScrollText,
  UserSearch,
} from 'lucide-react';

import { AGENT_SIDEBAR_DEFINITIONS } from './sidebar-definitions.agent';
import { WORKSPACE_SIDEBAR_DEFINITIONS } from './sidebar-definitions.workspace';

import type { SidebarDefinition } from './sidebar-definition.types';

export type { SidebarDefinition, SidebarGroup } from './sidebar-definition.types';

/**
 * Sidebar IA in 4 labelled groups — AGENT / SURFACES / OBSERVABILITY /
 * ORG & SETTINGS (A17 — the operator IA audit's PLATFORM regroup, since
 * consolidated). The former 17-item flat PLATFORM block was split so every
 * group stays inside the ~7±2 scannable limit; the spec's IA section
 * (`product/product-experience/dashboard.md`) is reconciled to this grouping.
 *
 * It was 7. `INSIGHTS` went when it emptied (below); `LIMITS` and `CONNECT`
 * went on 2026-08-28 when each was down to a single row — a heading shouting a
 * word the one link beneath it repeated. `SidebarGroup` carries that
 * reasoning.
 *
 * The `INSIGHTS` group is GONE. It held the two operator-curation surfaces the
 * agent's own signal produced — the red-team report (removed by ADR-0532) and
 * the catalog-review queue (ADR-0534) — and the two removals landed in parallel
 * on separate branches, so neither saw that between them the group lost every
 * member. Nothing claims it now, so the group code, its `GROUP_LABEL_KEY` entry
 * and its `sidebar.groupInsights` heading are removed rather than left as a
 * taxonomy slot no row can reach; `SidebarGroup` is exhaustive over the record,
 * so the compiler holds those two together. A future curation surface adds the
 * group back with its first row.
 *
 * `LIMITS` and `CONNECT` went the same way and one step earlier, at ONE member
 * rather than at zero: `/limits` (which merged spend caps, usage alarms and rate
 * limits) joins ORG beside Billing, and `/connections` joins AGENT. A heading
 * shouting a word the single link beneath it repeats is four lines of chrome for
 * one row, and it tells a reader the taxonomy is bigger than it is. Their codes,
 * `GROUP_LABEL_KEY` entries and `sidebar.groupLimits` / `sidebar.groupConnect`
 * headings are removed with them, ×6.
 *
 * URL rule (A17): the `/settings/*` prefix is reserved for org/workspace
 * CONFIGURATION; first-class working surfaces live at root (custom-endpoints
 * and the widget surface moved to `/custom-endpoints` + `/widget`, with
 * redirects from the old paths in `routes.tsx`).
 *
 * AF-10b: labels route through `useT()`; the route + group structure stays
 * static. AF-432: every item carries a Lucide icon.
 */
export const SIDEBAR_DEFINITIONS: readonly SidebarDefinition[] = [
  { to: '/home', labelKey: 'sidebar.overview', icon: Home, surface: 'overview' },

  ...AGENT_SIDEBAR_DEFINITIONS,

  // Surfaces rework: the Design Studio leads (`/design` — the profile library
  // the operator lands on; surface renamed from "Design Profiles" 2026-07-02,
  // the items are still "profiles"); the per-profile editor is no longer a nav
  // surface — it is reached only via a profile's "Edit" action (which
  // deep-links to `/widget?design=<id>`), so it carries no sidebar entry.
  {
    to: '/design',
    labelKey: 'sidebar.designProfiles',
    icon: Palette,
    group: 'SURFACES',
    surface: 'surfaces',
  },
  // Engagement — the merge of Chat starters, Proactive nudges and Page rules
  // into ONE row. The three were a single visitor experience split across three
  // surfaces, and the split cost the sidebar three of its six SURFACES slots to
  // say what is now one word. Their paths redirect to the tab that absorbed
  // them, so a bookmark still lands somewhere true.
  {
    to: '/engagement',
    labelKey: 'sidebar.engagement',
    icon: Megaphone,
    group: 'SURFACES',
    surface: 'surfaces',
  },
  {
    to: '/playground',
    labelKey: 'sidebar.playground',
    icon: FlaskConical,
    group: 'SURFACES',
    surface: 'playground',
  },
  // Adaptive Compositions D1's `/components` showroom is RETIRED (2026-08-28,
  // operator decision). Its block catalogue was reference material rather than a
  // control surface, its preview console moved to `/experience` beside the
  // settings that shape it, and its one real control — the registered-component
  // promote/enable switches — moved into the Design Studio, which is the only
  // surface that shows a registered component. SURFACES is back to 5, one under
  // the A17 ≤6 scannable cap.
  // Adaptive Compositions D4 — the operator's composition controls. Labs-gated
  // (`experimentalFlag`), so it is invisible to a workspace that has not turned
  // compositions on and the default-visible SURFACES count stays within the A17
  // ≤6 scannable cap. That gating is also honest rather
  // than merely convenient: with the feature off the page has nothing to
  // configure — no welcome to tune, no spend to report — and its one useful
  // control, the kill switch, is the Labs entry itself and stays reachable at
  // `/settings/experimental`. The card says so on its face, so an operator who
  // switches it off here knows where the switch went.
  //
  // FIRST user of `experimentalFlag`. The record in `use-sidebar-items.ts` must
  // carry a matching entry or the row is hidden forever, which looks exactly
  // like a routing bug.
  {
    to: '/experience',
    labelKey: 'sidebar.chatExperience',
    icon: LayoutTemplate,
    group: 'SURFACES',
    surface: 'settings',
    experimentalFlag: ADAPTIVE_COMPOSITIONS_LABS_FEATURE_ID,
  },

  // The business reading of the agent, in four rooms. It leads OBSERVABILITY
  // rather than sitting under AGENT, because the question it answers — "is
  // this working?" — is the one a non-technical owner asks first, and
  // `/monitoring` beside it is the same question asked technically.
  {
    to: '/analytics',
    labelKey: 'sidebar.analytics',
    icon: Gauge,
    group: 'OBSERVABILITY',
    surface: 'analytics',
  },
  {
    to: '/monitoring',
    labelKey: 'sidebar.monitoring',
    icon: Activity,
    group: 'OBSERVABILITY',
    surface: 'analytics',
  },
  {
    to: '/conversation-logs',
    labelKey: 'sidebar.conversationLogs',
    icon: MessagesSquare,
    group: 'OBSERVABILITY',
    surface: 'analytics',
  },
  // F4-5 / ADR-0192 — the per-end-user learning surface. Conceptually a Frontier
  // control sibling of /governance (LIMITS), but AGENT and LIMITS both sit at the
  // committed ≤6 scannable limit (A17); OBSERVABILITY has room and this is the
  // per-end-user observation surface (learned profiles + segments), a natural
  // neighbour of conversation-logs. Operator may relocate.
  {
    to: '/users',
    labelKey: 'sidebar.users',
    icon: UserSearch,
    group: 'OBSERVABILITY',
    surface: 'analytics',
  },
  // The audit trail and the usage/cost log are one page with two tabs. ONE row,
  // not two: the two questions arrive from the same reader in the same sitting,
  // and a nav that made them choose first was asking them to already know which
  // of the two had recorded the thing they were looking for.
  //
  // The row keeps the `audit-log` surface gate, which is the STRICTER of the
  // two it replaces — audit read is `audit-log:view`, invisible to the viewer
  // role and viewable by editor and above (server: audit-log.resolver class
  // gate). Gating the merged row by the weaker of the two would have handed the
  // viewer role a link to a page whose first tab it cannot read.
  {
    to: '/system-logs',
    labelKey: 'sidebar.systemLogs',
    icon: ScrollText,
    group: 'OBSERVABILITY',
    surface: 'audit-log',
  },

  // TG-3b (frigade-parity) — the Task Guide curation queue.
  //
  // SURFACES rather than AGENT, and the group was not a free choice: AGENT renders
  // six rows and the A17 cap is six (`AppShell.test.tsx` — "keeps every group within
  // the ~7±2 scannable limit"). SURFACES is the right second home rather than a
  // consolation — a guide is a thing a VISITOR is walked through, which is what this
  // group already collects (`/engagement`'s nudges, `/experience`'s chat surface).
  {
    to: '/task-guides',
    labelKey: 'sidebar.taskGuides',
    icon: Route,
    group: 'SURFACES',
    // `catalog:view` — the curation queue reads through the task-guide
    // resolvers, which gate on it. An Analyst therefore does not see this row,
    // which is right: curating guides is not the customer-success seat's job.
    surface: 'catalog',
  },
  // REP-1 — saved reports over the value-free aggregates.
  //
  // The one placement in this file that is a COMPROMISE rather than a fit, named
  // rather than left for a reader to wonder about: a report belongs beside
  // `/funnels` in OBSERVABILITY, and that group is at the cap once `/funnels` takes
  // its sixth slot. SURFACES had the only remaining room. If the nav is re-grouped,
  // this is the first row to move.
  {
    to: '/reports',
    labelKey: 'sidebar.reports',
    icon: FileBarChart,
    group: 'SURFACES',
    // `analytics:view` — saved reports read the value-free aggregates, and the
    // page's own edit control already asks for `analytics:admin`.
    surface: 'analytics',
  },
  // FUN-3 — operator-defined funnels. OBSERVABILITY's sixth and last slot under the
  // A17 cap; it is measurement of what visitors actually do, so it sits with the
  // other measurement rows rather than with the agent's own configuration.
  {
    to: '/funnels',
    labelKey: 'sidebar.funnels',
    icon: Filter,
    group: 'OBSERVABILITY',
    // `analytics:view` — measurement of what visitors actually do.
    surface: 'analytics',
  },
  ...WORKSPACE_SIDEBAR_DEFINITIONS,
];

export { GROUP_LABEL_KEY } from './sidebar-groups';
